import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  PREVIEW_DATA_TYPE,
  PreviewDataProvider,
  RecipientTypes,
  stepDataForPartnerEmail,
} from '@galaxy/campaign/dependencies';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { MarketsService } from '../../core/markets/markets.service';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { GENERIC_ACCOUNT_GROUP } from '../../marketing/marketing-automation-page/campaigns.service';

const PAGE_SIZE = 20;

@Component({
  templateUrl: './campaign-account-selector.component.html',
  imports: [
    CommonModule, //
    GalaxyInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    MatOptionModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
  ],
})
export class CampaignAccountSelectorComponent implements PreviewDataProvider, OnInit {
  data$: PREVIEW_DATA_TYPE;

  accountGroupSelect: UntypedFormControl = new UntypedFormControl();
  readonly accountGroups$: Observable<AccountGroup[]>;
  genericAccountGroup = GENERIC_ACCOUNT_GROUP;
  selectedAccountGroup: AccountGroup;
  searchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  private accessibleMarkets: string[] | undefined;

  constructor(
    @Inject('USER_ID') uid$: Observable<string>,
    private readonly accountGroupService: AccountGroupService,
    private readonly marketsService: MarketsService,
  ) {
    this.accountGroups$ = this.accountGroupService.accountGroups;
    this.data$ = combineLatest([this.accountGroupSelect.valueChanges, uid$]).pipe(
      map(([ag, uid]: [AccountGroup, string]) =>
        stepDataForPartnerEmail(ag.accountGroupId, RecipientTypes.IAMUser, uid),
      ),
    );
  }

  async ngOnInit(): Promise<void> {
    const markets = await firstValueFrom(this.marketsService.markets);
    this.accessibleMarkets = markets.map((m) => m.market_id);
    await firstValueFrom(
      this.accountGroupService.lookup(
        new ProjectionFilter({
          napData: true,
        }),
        this.accessibleMarkets,
        '',
        undefined,
        PAGE_SIZE,
      ),
    );
  }

  selectAccount(accountGroup?: AccountGroup): void {
    if (!accountGroup) {
      this.accountGroupSelect.setValue(null);
      this.selectedAccountGroup = null;
      return;
    }
    this.selectedAccountGroup = accountGroup;
  }

  displayFn(accountGroup?: AccountGroup): string | undefined {
    return accountGroup ? accountGroup.napData.companyName : 'Generic account';
  }

  updateSearchTerm(event: Event): void {
    this.selectedAccountGroup = null;
    const searchValue = (event.target as HTMLInputElement).value;
    this.searchTerm$$.next(searchValue);
    this.search(searchValue);
  }

  search(searchValue: string): void {
    this.accountGroupService
      .lookup(
        new ProjectionFilter({
          napData: true,
        }),
        this.accessibleMarkets,
        searchValue,
        undefined,
        PAGE_SIZE,
      )
      .pipe(take(1));
  }
}
